/**
 * @author Красильников Даниил <dkrasilnikov@gmail.com>
 * @link https://t.me/krasilneg
 * 
 * @author Колесник Данил <90.kalias.3@gmail.com>
 * @link https://t.me/kalias_90
 */

import React, { ChangeEvent, ClassAttributes, useState, PropsWithChildren } from 'react';
import { isChrome } from 'react-device-detect';
import { styled } from '@mui/material/styles';
import Scrollable from '@/components/Scrollable.js';
import ViewerWindowContentList from './ViewerWindowContentList.js';
import { RootState, useAppSelector } from '@/state/store';
import { saveExpandedSettingGroups, setDevelopmentSettingsVisibility, setSetting } from '@/actions/client';
import CustomSwitch from '@/components/CustomSwitch.js';
import { Action, ViewerControllerInterface, ViewerSettings as Settings } from '@bimdata/viewer-react';
import { createSelector } from '@reduxjs/toolkit';
import t from '@/utils/translate';
import { isNumeric } from 'validator';

window.setDevelopmentSettingsVisibility = (visible: boolean) => {
  setDevelopmentSettingsVisibility(visible);
};

const StyledSelect = styled('select')({
  height: 24,
  textAlign: 'left',
  padding: '0px 4px',
  width: '100%',
  borderColor: '#C1C7D0',
  borderStyle: 'solid',
  borderWidth: '1px',
  borderRadius: '4px',
  outline: 'none',
  '&:focus': {
    borderColor: '#0052CC',
  }
});

const StyledInput = styled('input')({
  height: 24,
  textAlign: 'center',
  padding: '4px 10px 4px 10px',
  width: 46,
  borderColor: '#C1C7D0',
  borderStyle: 'solid',
  borderWidth: '1px',
  borderRadius: '4px',
  outline: 'none',
  '&:focus': {
    borderColor: '#0052CC',
  }
});

type SettingInputProps = ClassAttributes<HTMLInputElement> & {
  value: string,
  validate?: (value: string) => boolean;
  onSubmit: (value: string) => string;
};

const SettingInput = (props: SettingInputProps) => {
  const [ v, setValue ] = useState(props.value);

  const { value, validate, onSubmit, ...inputProps } = props;

  return <StyledInput
    {...inputProps}
    value={v}
    onChange={(event) => {
      if (!validate || validate(event.target.value)) {
        setValue(event.target.value);
      }
    }}
    onKeyDown={(event) => {
      if (event.key.toUpperCase() == 'ENTER') {
        (event.target as HTMLInputElement).blur();
      }
    }}
    onBlur={() => {
      const nv = onSubmit(v);
      setValue(nv);
    }}
  />
}

type SettingSelectProps = ClassAttributes<HTMLSelectElement> & PropsWithChildren & {
  value: string | number | string[],
  onSubmit: (value: string | number | string[]) => string | number | string[];
};

const SettingSelect = (props: SettingSelectProps) => {
  const [ v, setValue ] = useState(props.value);

  const { value, onSubmit, ...inputProps } = props;

  return <StyledSelect
    {...inputProps}
    value={v}
    onChange={(event) => {
      setValue(onSubmit(event.target.value));
    }}
    onKeyDown={(event) => {
      if (event.key.toUpperCase() == 'ENTER') {
        (event.target as HTMLInputElement).blur();
      }
    }}
  />
}

const ViewerSettings = ({ viewer }: { viewer: ViewerControllerInterface }) => {
  const {
    opened, devSettingsVisibility, settings
  } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.authentication.clientState.expandedSettingGroups || {},
        (state: RootState) => state.authentication.clientState.developmentSettingsVisibility,
        (state: RootState) => state.authentication.clientState.settings as Settings,
      ],
      (opened, devSettingsVisibility, settings) => ({ opened: opened || {}, devSettingsVisibility, settings })
    )  
  );

  return (
    <Scrollable id="settingsList">
      <ViewerWindowContentList
        groups={
          [
            {
              code: 'controls',
              name: t('viewer.settings.title.controls'),
              hidden: false,
              list: [
                /*
                {
                  code: 'wheelAccelerationMode',
                  name: t('viewer.settings.label.acceleration'),
                  value: <CustomSwitch
                    checked={!!settings.wheelAccelerationMode}
                    onChange={(event) => {
                      saveSetting('wheelAccelerationMode', event.target.checked ? 2 : 0);
                      viewer.action(Action.SET_WHEEL_ACCELERATION_MODE, event.target.checked ? 2 : 0);
                    }}
                  />,
                },
                {
                  code: 'zoomSmoothness',
                  name: t('viewer.settings.label.smoothZoom'),
                  value: <CustomSwitch
                    disabled
                    checked={!!settings.zoomSmoothness}
                    onChange={(event) => {
                      saveSetting('zoomSmoothness', event.target.checked);
                    }}
                  />,
                },
                */
                {
                  code: 'wheelInversion',
                  name: t('viewer.settings.label.invertZoom'),
                  value: <CustomSwitch
                    checked={!!settings.wheelInversion}
                    onChange={(event: ChangeEvent) => {
                      setSetting('wheelInversion', (event.target as HTMLInputElement).checked);
                      viewer.action(Action.SET_WHEEL_INVERSION, (event.target as HTMLInputElement).checked);
                    }}
                  />,
                },
                {
                  code: 'enableHorizontalRotationInversion',
                  name: t('viewer.settings.label.invertHorizontalTurn'),
                  hidden: !devSettingsVisibility,
                  value: <CustomSwitch
                    checked={!!settings.enableHorizontalRotationInversion}
                    onChange={(event: ChangeEvent) => {
                      setSetting('enableHorizontalRotationInversion', (event.target as HTMLInputElement).checked);
                      viewer.action(Action.SET_ENABLE_HORIZONTAL_ROTATION_INVERSION, (event.target as HTMLInputElement).checked);
                    }}
                  />,
                },
                {
                  code: 'fpvVerticalRotationInversion',
                  name: t('viewer.settings.label.invertLBVertical'),
                  hidden: !devSettingsVisibility,
                  hint: t('viewer.settings.hint.invertLBVertical'),
                  value: <CustomSwitch
                    checked={!!settings.fpvVerticalRotationInversion}
                    onChange={(event: ChangeEvent) => {
                      setSetting('fpvVerticalRotationInversion', (event.target as HTMLInputElement).checked);
                      viewer.action(Action.SET_FPV_VERTICAL_ROTATION_INVERSION, (event.target as HTMLInputElement).checked);
                    }}
                  />,
                },
                {
                  code: 'fpvHorizontalRotationInversion',
                  name: t('viewer.settings.label.invertLBHorizontal'),
                  hidden: !devSettingsVisibility,
                  hint: t('viewer.settings.hint.invertLBHorizontal'),
                  value: <CustomSwitch
                    checked={!!settings.fpvHorizontalRotationInversion}
                    onChange={(event: ChangeEvent) => {
                      setSetting('fpvHorizontalRotationInversion', (event.target as HTMLInputElement).checked);
                      viewer.action(Action.SET_FPV_HORIZONTAL_ROTATION_INVERSION, (event.target as HTMLInputElement).checked);
                    }}
                  />,
                },
                {
                  code: 'fpvVerticalPanInversion',
                  name: t('viewer.settings.label.invertRBVertical'),
                  hidden: !devSettingsVisibility,
                  value: <CustomSwitch
                    checked={!!settings.fpvVerticalPanInversion}
                    onChange={(event: ChangeEvent) => {
                      setSetting('fpvVerticalPanInversion', (event.target as HTMLInputElement).checked);
                      viewer.action(Action.SET_FPV_VERTICAL_PAN_INVERSION, (event.target as HTMLInputElement).checked);
                    }}
                  />,
                },
                {
                  code: 'fpvHorizontalPanInversion',
                  name: t('viewer.settings.label.invertRBHorizontal'),
                  hidden: !devSettingsVisibility,
                  value: <CustomSwitch
                    checked={!!settings.fpvHorizontalPanInversion}
                    onChange={(event: ChangeEvent) => {
                      setSetting('fpvHorizontalPanInversion', (event.target as HTMLInputElement).checked);
                      viewer.action(Action.SET_FPV_HORIZONTAL_PAN_INVERSION, (event.target as HTMLInputElement).checked);
                    }}
                  />,
                }
              ]
            },
            {
              code: 'view',
              name: t('viewer.settings.title.view'),
              hidden: false,
              list: [
                {
                  code: 'wireframe',
                  name: t('viewer.settings.label.wireframe'),
                  hidden: !devSettingsVisibility,
                  value: <CustomSwitch
                    checked={!!settings.wireframe}
                    onChange={(event: ChangeEvent) => {
                      setSetting('wireframe', (event.target as HTMLInputElement).checked);
                      viewer.action(Action.SET_WIREFRAME, (event.target as HTMLInputElement).checked);
                    }}
                  />,
                },
                {
                  code: 'edges',
                  name: t('viewer.settings.label.edges'),
                  hidden: false,
                  value: <CustomSwitch
                    checked={!!settings.edges}
                    onChange={(event: ChangeEvent) => {
                      setSetting('edges', (event.target as HTMLInputElement).checked);
                      viewer.action(Action.SET_EDGE_VISIBILITY, (event.target as HTMLInputElement).checked);
                    }}
                  />,
                },
                {
                  code: 'boundBoxes',
                  name: t('viewer.settings.label.boundboxes'),
                  hidden: !devSettingsVisibility,
                  value: <CustomSwitch
                    checked={!!settings.boundBoxes}
                    onChange={(event: ChangeEvent) => {
                      setSetting('boundBoxes', (event.target as HTMLInputElement).checked);
                      viewer.action(Action.SET_BOUND_BOXES_VISIBILITY, (event.target as HTMLInputElement).checked);
                    }}
                  />,
                },
                {
                  code: 'failSafeMode',
                  name: t('viewer.settings.label.failSafeMode'),
                  hidden: !devSettingsVisibility,
                  value: <CustomSwitch
                    value={settings.failSafe}
                    checked={!!settings.failSafe}
                    onChange={(event: ChangeEvent) => {
                      if (confirm(t('viewer.settings.message.reload'))) {
                        setSetting('failSafe', (event.target as HTMLInputElement).checked);
                        location.reload();
                      }
                    }}                    
                  />
                },
              ]
            },
            {
              code: 'FPSOptimization',
              name: t('viewer.settings.title.optimization'),
              hidden: !devSettingsVisibility,
              list: [
                {
                  code: 'monolithLimit',
                  name: t('viewer.settings.label.monoliths'),
                  value: <SettingInput
                    value={settings.monolithLimit ? `${settings.monolithLimit}` : '0'}
                    validate={(value) => !isNaN(Number(value))}
                    onSubmit={(v) => {
                      const value = (v && isNumeric(v)) ? Math.round(Number(v)) : 0;
                      if (
                        value !== settings.monolithLimit
                        && confirm(t('viewer.settings.message.reload'))
                      ) {
                        setSetting('monolithLimit', value);
                        location.reload();
                        return `${value}`;
                      } else {
                        return `${settings.monolithLimit}`;
                      }
                    }}
                  />
                },
                {
                  code: 'targetFramerate',
                  name: t('viewer.settings.label.progressive'),
                  value: <SettingInput
                    value={settings.targetFramerate ? `${settings.targetFramerate}` : ''}
                    validate={(value) => !isNaN(Number(value))}
                    onSubmit={(v) => {
                      const value = Number(v);
                      if (value !== settings.targetFramerate) {
                        setSetting('targetFramerate', value);
                        viewer.setTargetFramerate(value);
                      }
                      return value.toString();
                    }}
                  />,
                },
                {
                  code: 'forbidWorker',
                  name: t('viewer.settings.label.worker'),
                  value: <CustomSwitch
                  // @ts-ignore
                    disabled={!isChrome || !window.Worker || !window.OffscreenCanvas}
                  // @ts-ignore
                    checked={isChrome && window.Worker && window.OffscreenCanvas && !settings.forbidWorker}
                    onChange={(event: ChangeEvent) => {
                      if (confirm(t('viewer.settings.message.reload'))) {
                        setSetting('forbidWorker', !(event.target as HTMLInputElement).checked);
                        location.reload();
                      }
                    }}
                  />,
                }
              ]
            },
            {
              code: 'LoadingOptions',
              name: t('viewer.settings.title.loading'),
              hidden: !devSettingsVisibility,
              list: [
                {
                  code: 'loadMode',
                  name: t('viewer.settings.label.loadMode'),
                  value: <SettingSelect
                    value={settings.loadMode || 0}
                    onSubmit={(v) => {
                      const value = Number(v);
                      if (value !== settings.loadMode) {
                        setSetting('loadMode', value);
                        viewer.setLoadMode(value);
                      }
                      return value;
                    }}
                  >
                    <option value={0}>{t('viewer.settings.option.modelFiles')}</option>
                    <option value={1}>BIM API</option>
                  </SettingSelect>,
                },
                {
                  code: 'skipTextures',
                  name: t('viewer.settings.label.skipTextures'),
                  value: <CustomSwitch
                  // @ts-ignore
                    checked={!!settings.skipTextures}
                    onChange={(event: ChangeEvent) => {
                      setSetting('skipTextures', (event.target as HTMLInputElement).checked);
                      viewer.setSkipTextures((event.target as HTMLInputElement).checked);
                    }}
                  />,
                },
                {
                  code: 'skipElementMaterials',
                  name: t('viewer.settings.label.skipElementMaterials'),
                  value: <CustomSwitch
                  // @ts-ignore
                    checked={!!settings.skipElementMaterials}
                    onChange={(event: ChangeEvent) => {
                      setSetting('skipElementMaterials', (event.target as HTMLInputElement).checked);
                      viewer.setSkipElementMaterials((event.target as HTMLInputElement).checked);
                    }}
                  />,
                },                                
                {
                  code: 'renderMode',
                  name: t('viewer.settings.label.renderMode'),
                  value: <SettingSelect
                    value={settings.renderMode || 0}
                    onSubmit={(v) => {
                      const value = Number(v);
                      if (value !== settings.renderMode) {
                        setSetting('renderMode', value);
                        viewer.setRenderMode(value);
                      }
                      return value;
                    }}
                  >
                    <option value={0}>{t('viewer.settings.option.default')}</option>
                    <option value={1}>{t('viewer.settings.option.noFrameIsolation')}</option>
                    <option value={2}>{t('viewer.settings.option.noRendering')}</option>
                  </SettingSelect>,
                }                
              ]
            }
          ].map((group) => ({
            ...group,
            opened: (group.code in opened) ? opened[group.code] : true
          }))
        }
        switchGroup={(code, value) => {
          saveExpandedSettingGroups({
            ...opened,
            [code]: value
          });
        }}
        forcedOpenAll={false}
      />
    </Scrollable>
  );
};

export default ViewerSettings;